import type { ComponentChildren } from "preact";
import { useState, useEffect, useRef } from "preact/hooks";
import { BrutalButton } from "./BrutalUI";
import BrutalModal from "./BrutalModal";
import type { AvatarCropPayload } from "../api";
import { uploadAvatar } from "../api";

interface AvatarPickerProps {
  slug: string;
  username: string;
  /** When provided, only the trigger and modal are rendered (no default "Set face" row). Second arg: open modal with file already selected (skips file picker step). */
  trigger?: (open: () => void, openWithFile: (file: File) => void) => ComponentChildren;
}

export default function AvatarPicker({ slug, username, trigger }: AvatarPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [cropSize, setCropSize] = useState(0);
  /** Zoom 0 = zoomed out (full crop), 100 = zoomed in (smallest crop). */
  const [zoom, setZoom] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Clean up object URL when component unmounts or image changes.
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const resetState = () => {
    setFile(null);
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setImageUrl(null);
    setImageWidth(0);
    setImageHeight(0);
    setCropX(0);
    setCropY(0);
    setCropSize(0);
    setZoom(0);
    setError(null);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    resetState();
  };

  const loadFile = (selected: File) => {
    if (!selected.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    setError(null);
    setFile(selected);
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    const url = URL.createObjectURL(selected);
    setImageUrl(url);

    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      setImageWidth(w);
      setImageHeight(h);
      const maxSize = Math.min(w, h);
      setCropSize(maxSize);
      setCropX((w - maxSize) / 2);
      setCropY((h - maxSize) / 2);
      setZoom(0);
    };
    img.onerror = () => {
      setError("Could not load image.");
    };
    img.src = url;
  };

  const handleFileChange = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const selected = input.files && input.files[0];
    if (!selected) return;
    loadFile(selected);
  };

  const openWithFile = (selectedFile: File) => {
    loadFile(selectedFile);
    setIsOpen(true);
  };

  // Draw 64x64 preview of the current crop.
  useEffect(() => {
    if (!imageUrl || !cropSize || !canvasRef.current) return;
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(
        img,
        cropX,
        cropY,
        cropSize,
        cropSize,
        0,
        0,
        canvas.width,
        canvas.height,
      );
    };
    img.src = imageUrl;
  }, [imageUrl, cropX, cropY, cropSize]);

  const handleCropChangeX = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const maxX = Math.max(0, imageWidth - cropSize);
    const v = Math.min(Math.max(0, Number(target.value) || 0), maxX);
    setCropX(v);
  };

  const handleCropChangeY = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const maxY = Math.max(0, imageHeight - cropSize);
    const v = Math.min(Math.max(0, Number(target.value) || 0), maxY);
    setCropY(v);
  };

  const maxCropSize = imageWidth && imageHeight ? Math.min(imageWidth, imageHeight) : 0;
  const minCropSize = 64;

  const handleZoomChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const newZoom = Math.min(100, Math.max(0, Number(target.value) || 0));
    if (maxCropSize <= minCropSize) {
      setZoom(newZoom);
      return;
    }
    const centerX = cropX + cropSize / 2;
    const centerY = cropY + cropSize / 2;
    const newCropSize = Math.max(
      minCropSize,
      maxCropSize - (newZoom / 100) * (maxCropSize - minCropSize),
    );
    const newCropX = Math.max(
      0,
      Math.min(imageWidth - newCropSize, centerX - newCropSize / 2),
    );
    const newCropY = Math.max(
      0,
      Math.min(imageHeight - newCropSize, centerY - newCropSize / 2),
    );
    setZoom(newZoom);
    setCropSize(newCropSize);
    setCropX(newCropX);
    setCropY(newCropY);
  };

  const handleSubmit = async () => {
    if (!file || !cropSize) {
      setError("Choose an image and crop area first.");
      return;
    }
    setIsUploading(true);
    setError(null);
    const crop: AvatarCropPayload = {
      x: Math.round(cropX),
      y: Math.round(cropY),
      size: Math.round(cropSize),
    };
    try {
      await uploadAvatar(slug, file, crop);
      handleClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setError("Failed to upload face image. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const triggerContent = trigger ? trigger(handleOpen, openWithFile) : (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-700 dark:text-gray-300">
          Face for <span className="font-semibold">{username || "you"}</span>
        </span>
        <BrutalButton
          type="button"
          variant="secondary"
          className="px-3 py-1 text-xs min-h-[2rem]"
          onClick={handleOpen}
        >
          Set face
        </BrutalButton>
      </div>
    </div>
  );

  return (
    <>
      {triggerContent}
      <BrutalModal
        open={isOpen}
        onClose={handleClose}
        title="Set your face"
        maxWidth="max-w-md"
      >
        <>
          {!file ? (
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                Choose an image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="text-xs"
              />
            </div>
          ) : null}
          {imageUrl && cropSize > 0 ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-xs text-gray-700 dark:text-gray-300">
                    Zoom
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={zoom}
                    onInput={handleZoomChange}
                    title="Zoom in/out"
                  />
                  <label className="text-xs text-gray-700 dark:text-gray-300 mt-2">
                    Horizontal position
                  </label>
                  <input
                    type="range"
                    min="0"
                    max={Math.max(0, imageWidth - cropSize)}
                    value={cropX}
                    onInput={handleCropChangeX}
                  />
                  <label className="text-xs text-gray-700 dark:text-gray-300 mt-2">
                    Vertical position
                  </label>
                  <input
                    type="range"
                    min="0"
                    max={Math.max(0, imageHeight - cropSize)}
                    value={cropY}
                    onInput={handleCropChangeY}
                  />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xs text-gray-700 dark:text-gray-300">
                    64×64 preview
                  </span>
                  <canvas
                    ref={canvasRef}
                    width={64}
                    height={64}
                    className="border-2 border-black dark:border-gray-100 bg-gray-200 dark:bg-gray-800"
                  />
                </div>
              </div>
            </div>
          ) : null}
          {error ? (
            <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
          ) : null}
          <div className="flex gap-2 justify-end">
            <BrutalButton
              type="button"
              variant="secondary"
              className="px-3 py-1 text-xs min-h-0"
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancel
            </BrutalButton>
            <BrutalButton
              type="button"
              variant="primary"
              className="px-3 py-1 text-xs min-h-0"
              onClick={handleSubmit}
              disabled={isUploading || !file || !cropSize}
            >
              {isUploading ? "Saving…" : "Save"}
            </BrutalButton>
          </div>
        </>
      </BrutalModal>
    </>
  );
}

